import { DOCUMENT_CACHE_QUERY_KEY } from '@constants/documents.constants';
import { SendingsDashboardType } from '@domain/types';

/**
 * The v1 document caches, keyed `[DOCUMENT_CACHE_QUERY_KEY, documentId, ...sub]`: one bundle per
 * document, plus a sub-cache per related collection the detail view loads separately.
 *
 * A neutral module rather than part of `caching.util.ts`, which imports the API layer and the
 * Redux store — the same reason `hasActiveFilters` lives in `dashboardFilters.util.ts`. Key
 * builders must be importable from anywhere, including every feature module that writes one.
 *
 * This module is the only place that knows the layout: build a key here, never spell one out. The
 * sub-keys extend `documentDetailKey` rather than repeating its segments, and that nesting is a
 * runtime contract, not tidiness: TanStack matches keys by prefix, so invalidating a document's
 * detail key invalidates its attachments and encounters too. `invalidateSendingRequestCache` takes
 * an `exact` flag precisely to opt out of that sweep. Flattening any sub-key out from under the
 * detail key would silently stop those refetches.
 *
 * Not here: `DOCUMENT_CACHE_BLOCKING_MUTATION_KEY` (`documents.constants.ts`), which shares this
 * prefix but keys the *mutation* cache, a different store — hence no collision with a document
 * whose id happened to be `'blocking'`.
 */
export const documentDetailKey = (documentId: string) => [DOCUMENT_CACHE_QUERY_KEY, documentId];

/** Attachments are fetched per dashboard: the same document carries different files on each. */
export const documentAttachmentsKey = (
  documentId: string,
  dashboardType: SendingsDashboardType,
) => [...documentDetailKey(documentId), 'attachments', dashboardType];

export const documentEncountersKey = (documentId: string) => [
  ...documentDetailKey(documentId),
  'encounters',
];

/**
 * Every document cache at once, sub-caches included — the prefix a full reset sweeps. Deliberately
 * shorter than `documentDetailKey`, which always fixes a document id.
 */
export const allDocumentCachesKey = () => [DOCUMENT_CACHE_QUERY_KEY];
